import { motion } from "framer-motion";
import { ArrowRight, Search, ListChecks, FolderCog, Send, Radar, Trophy } from "lucide-react";

const STEPS = [
  { icon: Search, num: "01", title: "UNDERSTAND", desc: "Understand the business, industry, location and proposed activities.", accent: "#FF8800" },
  { icon: ListChecks, num: "02", title: "IDENTIFY", desc: "Identify registrations, licenses and certifications that may apply.", accent: "#00B0FF" },
  { icon: FolderCog, num: "03", title: "PREPARE", desc: "Help organize forms, documents and required information.", accent: "#00E676" },
  { icon: Send, num: "04", title: "APPLY", desc: "Coordinate the application process with the appropriate authority or professional.", accent: "#FF8800" },
  { icon: Radar, num: "05", title: "TRACK", desc: "Help track application progress and respond to documentation requirements.", accent: "#00B0FF" },
  { icon: Trophy, num: "06", title: "COMPLETE", desc: "Help the entrepreneur organize the completed registration and certification records.", accent: "#00E676" },
];

const FLOW = ["BUSINESS", "DOCUMENTS", "APPLICATION", "AUTHORITY", "APPROVAL / CERTIFICATE"];

export function RegProcess() {
  return (
    <section data-testid="reg-process-section" className="relative py-20 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-14"
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">The Process</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white" data-testid="reg-process-headline">
            HOW <span className="text-gradient-saffron">LAUNCHLAB WORKS</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6 lg:gap-4" data-testid="reg-process-steps">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`reg-process-step-${s.num}`}
            >
              <div
                className="w-[104px] h-[104px] rounded-2xl glass flex flex-col items-center justify-center mx-auto lg:mx-0 transition-transform duration-300 hover:scale-110 hover:-translate-y-1"
                style={{ boxShadow: `0 0 30px ${s.accent}18` }}
              >
                <s.icon className="w-6 h-6 mb-1.5" style={{ color: s.accent }} strokeWidth={1.8} />
                <span className="font-mono-tech text-[10px] tracking-[0.25em] text-slate-500">STEP</span>
                <span className="font-display font-extrabold text-lg" style={{ color: s.accent }}>{s.num}</span>
              </div>
              <div className="mt-5 text-center lg:text-left">
                <h3 className="font-display font-bold tracking-[0.14em] text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 glass rounded-2xl p-6 sm:p-8"
          data-testid="reg-process-flow"
        >
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-3">
            {FLOW.map((f, i) => (
              <div key={f} className="flex items-center gap-3">
                <span
                  className={`px-4 py-2.5 rounded-lg font-display font-semibold text-xs sm:text-sm tracking-wide ${
                    i === FLOW.length - 1
                      ? "bg-[#00E676]/10 border border-[#00E676]/40 text-[#00E676] shadow-[0_0_25px_rgba(0,230,118,0.15)]"
                      : "glass text-white"
                  }`}
                >
                  {f}
                </span>
                {i < FLOW.length - 1 && <ArrowRight className="w-4 h-4 text-[#FF8800]" />}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
